import type { UrlObject } from 'url'
import { Box, Button, Container, Heading, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faClipboardCheck,
  faCommentAltSmile,
  faCookieBite,
  faShieldAlt,
  faShieldCheck,
  faSpellCheck,
  faUserShield,
} from '@fortawesome/pro-duotone-svg-icons'
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { makeServerProps } from '@/server/query'

import makePage from '@/client/services/pages/makePage'
import * as url from '@/client/services/url'

import Section from '@/components/Landing/Section'
import Layout from '@/components/Layout/Layout'
import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'

const PRIVACY_RESOURCES = [
  {
    title: 'Privacy Principles',
    description: 'A human-readable version of our Privacy Policy',
    icon: faUserShield,
    iconPrimaryColor: 'green.solid',
    iconSecondaryColor: 'green.emphasized',
    src: {
      pathname: url.getAboutUrl('privacy'),
      hash: 'highlights',
    },
    isExternal: false,
  },
  {
    title: 'Privacy Policy',
    description: 'Our full Privacy Policy (the one written by lawyers!)',
    icon: faShieldAlt,
    iconPrimaryColor: 'green.emphasized',
    iconSecondaryColor: 'green.solid',
    src: {
      pathname: url.getAboutUrl('privacy'),
      hash: 'policy',
    },
    isExternal: false,
  },
  {
    title: 'Cookie Policy',
    description: 'Our policy outlining the use of cookies',
    icon: faCookieBite,
    iconPrimaryColor: 'orange.500',
    iconSecondaryColor: 'orange.200',
    src: url.getAboutUrl('cookies'),
    isExternal: true,
  },
  {
    title: 'Third Party Service Providers',
    description: 'A list of thirty party services we leverage and the data we share with them',
    icon: faClipboardCheck,
    iconPrimaryColor: 'cyan.solid',
    iconSecondaryColor: 'cyan.emphasized',
    src: url.getAboutUrl('third-parties'),
    isExternal: true,
  },
  {
    title: 'Terms and Definitions',
    description: 'The definitions for key terms in the other documents',
    icon: faSpellCheck,
    iconPrimaryColor: 'purple.solid',
    iconSecondaryColor: 'purple.emphasized',
    src: url.getAboutUrl('terms-definitions'),
    isExternal: true,
  },
]

const SAFETY_RESOURCES = [
  {
    title: 'Safety Measures',
    description: 'How we keep virtual tutoring safe',
    icon: faShieldCheck,
    iconPrimaryColor: 'green.solid',
    iconSecondaryColor: 'green.emphasized',
    src: url.getAboutUrl('safety'),
    isExternal: true,
  },
  {
    title: 'Session Content Policy',
    description: 'The content policies that keep the platform safe and inclusive',
    icon: faCommentAltSmile,
    iconPrimaryColor: 'cyan.solid',
    iconSecondaryColor: 'cyan.emphasized',
    src: url.getAboutUrl('content-policy'),
    isExternal: true,
  },
]

function Resource({
  title,
  description,
  icon,
  iconPrimaryColor,
  iconSecondaryColor,
  src,
  isExternal,
}: {
  title: string
  description: string
  icon: IconProp
  iconPrimaryColor: string
  iconSecondaryColor: string
  src: string | UrlObject
  isExternal: boolean
}): ReactElement {
  return (
    <ShwLink isExternal href={src} width="100%">
      <Stack
        direction="column"
        spacing={2}
        align="center"
        textAlign="center"
        flex={1}
        position="relative"
        pt={6}
        pr={4}
        pl={4}
        pb={8}
        height="100%"
        borderRadius="8px"
        border="2px solid"
        borderColor="border.emphasized"
        bgColor="bg"
        _hover={{
          cursor: 'pointer',
          bgColor: 'bg.subtle',
          borderColor: 'primary.focusRing',
        }}
        _active={{
          bgColor: 'bg.muted',
          borderColor: 'border.focusRing',
        }}
      >
        {isExternal ? (
          <FontAwesomeIcon
            icon={faExternalLink}
            color={chakraTokenToVar('fg.subtle')}
            style={{
              position: 'absolute',
              right: '24px',
              top: '24px',
            }}
          />
        ) : null}
        <FontAwesomeIcon
          icon={icon}
          style={{
            fontSize: '50px',
            margin: '10px',
            ...({
              '--fa-primary-opacity': 1,
              '--fa-secondary-opacity': 1,
              '--fa-primary-color': `${chakraTokenToVar(iconPrimaryColor)}`,
              '--fa-secondary-color': `${chakraTokenToVar(iconSecondaryColor)}`,
            } as Record<string, unknown>),
            // ^ CSS variables are not supported by the typing here
          }}
        />
        <Heading size="lg" as="h3" pt={2}>
          {title}
        </Heading>
        <Text color="fg.muted">{description}</Text>
      </Stack>
    </ShwLink>
  )
}

function TrustSafetyCenter(): ReactElement {
  return (
    <Layout title="Trust and Safety Center">
      <Box bg="primary.solid" position="relative" zIndex={0}>
        <Container>
          <Box maxWidth="600px" py={8}>
            <Text variant="eyebrow" mb={3} color="cyan.muted">
              Welcome to the
            </Text>
            <Heading size="3xl" as="h1" mb={3} color="fg.inverted">
              Trust &amp; Safety Center
            </Heading>
            <Text color="fg.inverted">
              Schoolhouse.world is a non-profit with the mission of connecting the world through
              learning. We&apos;re committed to upholding the higest standards in privacy, safety,
              and security.
            </Text>
          </Box>
        </Container>
        <PublicImage
          position="absolute"
          bottom={0}
          right={0}
          zIndex={-1}
          filter="hue-rotate(331deg)"
          src="/community-globe.svg"
        />
      </Box>

      <Section bg="bg.subtle" py={6}>
        <Stack justify="center" direction="column" align="center" spacing={12}>
          <Heading size="2xl" as="h2">
            Privacy
          </Heading>
          <Box>
            <SimpleGrid columns={[1, 1, 1, 3]} spacingY={8} spacingX={8} alignItems="stretch">
              {PRIVACY_RESOURCES.map(resource => (
                <Resource key={resource.title} {...resource} />
              ))}
            </SimpleGrid>
          </Box>
          <Box>
            <ShwLink isExternal href={url.getAboutUrl('privacy')}>
              <Button variant="outline">Read our Privacy Policy</Button>
            </ShwLink>
          </Box>
        </Stack>
      </Section>

      <Section py={6}>
        <Stack justify="center" direction="column" align="center" spacing={12}>
          <Heading size="2xl" as="h2">
            Safety
          </Heading>
          <Box>
            <SimpleGrid columns={[1, 1, 1, 3]} spacingY={8} spacingX={8} alignItems="stretch">
              {SAFETY_RESOURCES.map(resource => (
                <Resource key={resource.title} {...resource} />
              ))}
            </SimpleGrid>
          </Box>
          <Box>
            <ShwLink href={url.faq.getArticleUrl('how-do-i-contact-the-schoolhouse-team')}>
              <Button
                variant="outline"
                rightIcon={
                  <FontAwesomeIcon icon={faExternalLink} color={chakraTokenToVar('primary.fg')} />
                }
              >
                Contact our safety team
              </Button>
            </ShwLink>
          </Box>
        </Stack>
      </Section>

      <Section bg="bg.subtle" py={6}>
        <Stack justify="center" direction="column" align="center" spacing={12}>
          <Heading size="2xl" as="h2">
            Security
          </Heading>
          <Text color="fg.muted" maxWidth="768px">
            In addition to running our own penetration testing, we have a bug bounty program for
            security researchers who find and report vulnerabilities. To learn more, please reach us
            at{' '}
            <ShwLink href={url.email.get('security')}>
              {url.email.get('security', { as: 'text' })}
            </ShwLink>
            .
          </Text>
          <Box>
            <ShwLink href={url.email.get('security')}>
              <Button
                variant="outline"
                rightIcon={
                  <FontAwesomeIcon icon={faExternalLink} color={chakraTokenToVar('primary.fg')} />
                }
              >
                Contact our security team
              </Button>
            </ShwLink>
          </Box>
        </Stack>
      </Section>
    </Layout>
  )
}

const TrustSafetyCenterPage = makePage(TrustSafetyCenter, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(TrustSafetyCenterPage)
export default TrustSafetyCenterPage
